// src/services/api/index.js
import axios from 'axios';
import { getWeaversWorldApiUrl, logger } from '../utils';

/**
 * Get CSRF token from cookie
 * Django requires this token for POST/PUT/PATCH/DELETE requests
 */
const getCsrfToken = () => {
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  
  for (let cookie of cookieArray) {
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return '';
};

/**
 * Factory function to create a fully configured API client
 * This is used for both the default export and when a fresh instance is needed
 */
export const getWeaversApi = () => {
  // Create axios instance with base configuration
  const instance = axios.create({
    baseURL: getWeaversWorldApiUrl(),
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Important for cookies and authentication
  });
  
  // Add CSRF token to all requests that modify data
  instance.interceptors.request.use(config => {
    // Add CSRF token for methods that modify data
    if (
      config.method === 'post' || 
      config.method === 'put' || 
      config.method === 'patch' || 
      config.method === 'delete'
    ) {
      // Update token on each request to ensure it's fresh
      const token = getCsrfToken();
      if (token) {
        config.headers['X-CSRFToken'] = token;
        logger.trace('API', 'Adding CSRF token to request');
      } else {
        logger.error('API', 'CSRF token not found in cookies');
      }
    }
    return config;
  });

  // Response middleware to unwrap the standardized data format
  // IMPORTANT: This only unwraps the axios response.data layer, NOT our API's standardized
  // data format. After this interceptor, responses will still have a "data" property that
  // contains our API response data. Components should still access fields via response.data.field
  instance.interceptors.response.use(
    response => {
      // Check if response has data and data.data structure (our standardized format)
      if (response.data && response.data.data !== undefined) {
        // Unwrap the data layer for a cleaner API
        logger.debug('API', 'Unwrapping standardized data format', {
          endpoint: response.config.url,
          hasData: true
        });
        response.data = response.data.data;
      }
      return response;
    },
    error => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // outside the range of 2xx
        
        // Check if error response also follows standardized format
        if (error.response.data && error.response.data.data !== undefined) {
          // Unwrap the data layer for consistency
          logger.debug('API', 'Unwrapping standardized error data format');
          error.response.data = error.response.data.data;
        }
        
        logger.error('API', `Error ${error.response.status}:`, error.response.data);
        
        // Special handling for authentication errors
        if (error.response.status === 401 || error.response.status === 403) {
          logger.error('API', 'Authentication error - user may need to log in');
        }
      } else if (error.request) {
        // The request was made but no response was received
        logger.error('API', 'No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        logger.error('API', 'Request setup error:', error.message);
      }
      return Promise.reject(error);
    }
  );
  
  // Return the fully configured instance
  return instance;
};

// Create the default API instance using the factory function
// This maintains backward compatibility while ensuring consistency
const api = getWeaversApi();

export default api;